/**
 * FileInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { AudioMetadata } from './audio_metadata'
import type { ImageMetadata } from './image_metadata'
import type { VideoMetadata } from './video_metadata'

/**
 * FileInfo
 */
export interface FileInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** R2存储路径 */
  key?: string
  /** 文件角色 */
  role?: string
  /** 文件哈希值(用于去重) */
  hash?: string
  /** 原始文件名 */
  name?: string
  /** 文件扩展名 */
  ext?: string
  /** 文件类型 */
  type?: string
  /** 存储前缀 */
  key_prefix?: string
  /** 文件大小(bytes) */
  size?: number
  /** MIME类型 */
  mime_type?: string
  /** 宽度(像素) */
  width?: number
  /** 高度(像素) */
  height?: number
  /** 时长(毫秒,视频/音频) */
  duration?: number
  /** 图片元数据 */
  image_metadata?: ImageMetadata
  /** 视频元数据 */
  video_metadata?: VideoMetadata
  /** 音频元数据 */
  audio_metadata?: AudioMetadata
  /** [图片/视频/音频]媒体内容创建时间 - EXIF拍摄时间/视频录制时间/音频发布时间,区别于文件created_at,NULL表示未提取 */
  media_created_at?: string
  /** 访问URL */
  url?: string
  /** 缩略图ID（图片） */
  thumb_id?: string
  /** 缩略图URL */
  thumb_url?: string
  /** 封面ID（视频） */
  cover_id?: string
  /** 封面URL（视频） */
  cover_url?: string
  /** 是否公开访问 */
  is_public?: boolean
  /** 上传用户ID */
  upload_user_id?: string
  /** 上传管理员ID */
  upload_staff_id?: string
  /** 上传设备ID */
  upload_device_id?: string
  /** 文件上传状态 */
  upload_status?: string
  /** 上传错误信息 */
  upload_error?: string
  /** 上传时间 */
  uploaded_at?: string
}

/**
 * FileInfo Schema
 */
export const FileInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    key: {
      description: 'R2存储路径',
      format: 'string',
      type: 'string',
      label: 'R2存储路径',
    },
    role: {
      description: '文件角色',
      format: 'cons.FileRole',
      type: 'string',
      label: '文件角色',
    },
    hash: {
      description: '文件哈希值(用于去重)',
      format: 'string',
      type: 'string',
      label: '文件哈希值',
    },
    name: {
      description: '原始文件名',
      format: 'string',
      type: 'string',
      label: '原始文件名',
    },
    ext: {
      description: '文件扩展名',
      format: 'string',
      type: 'string',
      label: '文件扩展名',
    },
    type: {
      description: '文件类型',
      format: 'cons.FileType',
      type: 'string',
      label: '文件类型',
    },
    key_prefix: {
      description: '存储前缀',
      format: 'cons.FileKeyPrefix',
      type: 'string',
      label: '存储前缀',
    },
    size: {
      description: '文件大小(bytes)',
      format: 'int64',
      type: 'integer',
      label: '文件大小',
    },
    mime_type: {
      description: 'MIME类型',
      format: 'string',
      type: 'string',
      label: 'MIME类型',
    },
    width: {
      description: '宽度(像素)',
      format: 'int',
      type: 'integer',
      label: '宽度',
    },
    height: {
      description: '高度(像素)',
      format: 'int',
      type: 'integer',
      label: '高度',
    },
    duration: {
      description: '时长(毫秒,视频/音频)',
      format: 'int64',
      type: 'integer',
      label: '时长',
    },
    image_metadata: {
      $ref: 'ImageMetadata',
      description: '图片元数据',
      label: '图片元数据',
    },
    video_metadata: {
      $ref: 'VideoMetadata',
      description: '视频元数据',
      label: '视频元数据',
    },
    audio_metadata: {
      $ref: 'AudioMetadata',
      description: '音频元数据',
      label: '音频元数据',
    },
    media_created_at: {
      description:
        '[图片/视频/音频]媒体内容创建时间 - EXIF拍摄时间/视频录制时间/音频发布时间,区别于文件created_at,NULL表示未提取',
      format: '*time.Time',
      type: 'string',
    },
    url: {
      description: '访问URL',
      format: 'string',
      type: 'string',
      label: '访问URL',
    },
    thumb_id: {
      description: '缩略图ID（图片）',
      format: 'string',
      type: 'string',
      label: '缩略图ID',
    },
    thumb_url: {
      description: '缩略图URL',
      format: 'string',
      type: 'string',
      label: '缩略图URL',
    },
    cover_id: {
      description: '封面ID（视频）',
      format: 'string',
      type: 'string',
      label: '封面ID',
    },
    cover_url: {
      description: '封面URL（视频）',
      format: 'string',
      type: 'string',
      label: '封面URL',
    },
    is_public: {
      description: '是否公开访问',
      format: 'bool',
      type: 'boolean',
      label: '是否公开访问',
    },
    upload_user_id: {
      description: '上传用户ID',
      format: 'string',
      type: 'string',
      label: '上传用户ID',
    },
    upload_staff_id: {
      description: '上传管理员ID',
      format: 'string',
      type: 'string',
      label: '上传管理员ID',
    },
    upload_device_id: {
      description: '上传设备ID',
      format: 'string',
      type: 'string',
      label: '上传设备ID',
    },
    upload_status: {
      description: '文件上传状态',
      format: 'cons.FileUploadStatus',
      type: 'string',
      label: '文件上传状态',
    },
    upload_error: {
      description: '上传错误信息',
      format: 'string',
      type: 'string',
      label: '上传错误信息',
    },
    uploaded_at: {
      description: '上传时间',
      format: '*time.Time',
      type: 'string',
      label: '上传时间',
    },
  },
  type: 'object',
}
